import { CheckCircleIcon, SpinnerIcon } from "@chakra-ui/icons";
import { Box, Spinner, Text, VStack } from "@chakra-ui/react";
import { assertNever } from "@syla/shared/types/helpers/errors";
import { DataSourceType } from "@syla/shared/types/models/DataSourceBase";
import { DataSourceWithNetworks } from "@syla/shared/types/responses/DataSourceWithNetworks";
import React from "react";
import { getEstimatedSyncTime } from "../helpers/getEstimatedSyncTime";
import { ImportMethod } from "../panels/ImportMethodSelectionPanel";
import { ImportStage } from "../workflows/ImportStage";

type ImportProgressStatusProps = {
  dataSource: DataSourceWithNetworks;
  stage: Extract<
    ImportStage,
    "initialising" | "importing" | "backgroundImporting" | "complete"
  >;
  importMethod: ImportMethod;
};

export function ImportProgressStatus({
  stage,
  dataSource,
  importMethod,
}: ImportProgressStatusProps): React.ReactElement {
  const content = getImportStatusContent({
    stage,
    dataSource,
    importMethod,
  });

  // The empty Box components are for centering the content vertically, slightly
  // above center, when the space is available.

  return (
    <VStack w="full" flex={1} px={8} py={4} spacing={0} overflowY="auto">
      <Box flex="0.8" />

      <VStack w="full" align="center" spacing={8}>
        {content.visual}

        <VStack w="full" spacing={4}>
          <Text fontSize="2xl" fontWeight="bold" textAlign="center">
            {content.title}
          </Text>

          <Text fontSize="lg" textAlign="center" whiteSpace="pre-line">
            {content.description}
          </Text>
        </VStack>
      </VStack>

      <Box flex="1.2" />
    </VStack>
  );
}

function getImportStatusContent({
  stage,
  dataSource,
  importMethod,
}: ImportProgressStatusProps): {
  title: string;
  description: string;
  visual: React.ReactElement;
} {
  const estimatedSyncTime = getEstimatedSyncTime(dataSource);

  if (stage === "initialising") {
    return {
      title: "Checking connection...",
      description:
        "We’re preparing your import and confirming everything is ready to go.",
      visual: <ImportSpinner />,
    };
  }

  if (stage === "backgroundImporting") {
    return {
      title: "We're importing your transactions",
      description: `You can keep using Syla while we import your transactions in the background.${
        estimatedSyncTime ?
          ` The estimated sync time is ${estimatedSyncTime}.`
        : ""
      }`,
      visual: <SpinnerIcon boxSize="5rem" color="green.500" />,
    };
  }

  if (stage === "importing") {
    if (importMethod === "Empty") {
      return {
        title: "Creating data source",
        description: `We’re adding your data source now.`,
        visual: <ImportSpinner />,
      };
    }

    return {
      title: "Importing transactions",
      description: `We’re importing your ${dataSource.type === DataSourceType.Custom ? "" : dataSource.name + " "}transactions now. Please keep this page open until the import is complete.`,
      visual: <ImportSpinner />,
    };
  }

  if (stage === "complete") {
    if (importMethod === "Empty") {
      return {
        title: "Data source added",
        description: "Your data source has been added.",
        visual: <CheckCircleIcon boxSize="5rem" color="green.500" />,
      };
    }

    return {
      title: "Import complete",
      description:
        "Your transactions have been imported and are ready to review.",
      visual: <CheckCircleIcon boxSize="5rem" color="green.500" />,
    };
  }

  assertNever(stage);
}

function ImportSpinner() {
  return (
    <Spinner
      color="green.500"
      emptyColor="green.200"
      thickness="10px"
      boxSize="5rem"
    />
  );
}
